import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TRANSLATE_LANGUAGES,
  applyDocumentDirection,
  changeDisplayLanguage,
  getSavedLanguage,
  initGoogleTranslate,
  isRtlLanguage,
  resetDisplayLanguage,
} from '../utils/googleTranslate';
import '../styles/google-translate.css';

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const saved = getSavedLanguage();
    applyDocumentDirection(saved);
    return saved;
  });

  useEffect(() => {
    initGoogleTranslate();
  }, []);

  useEffect(() => {
    if (i18n.language !== 'en') {
      i18n.changeLanguage('en');
    }
  }, [i18n]);

  const changeLanguage = useCallback((lng) => {
    if (!lng || lng === currentLanguage) return;
    setCurrentLanguage(lng);
    changeDisplayLanguage(lng);
  }, [currentLanguage]);

  const value = useMemo(
    () => ({
      currentLanguage,
      changeLanguage,
      resetLanguage: resetDisplayLanguage,
      availableLanguages: TRANSLATE_LANGUAGES,
      isRtl: isRtlLanguage(currentLanguage),
    }),
    [currentLanguage, changeLanguage]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
